/**
 * `/disclaimer/{version}` — has the signed-in person accepted, and recording it.
 *
 * The check **fails open to asking**: no answer from the server shows the
 * disclaimer, because showing it to someone who already agreed costs a second,
 * and not showing it to someone who never did is the thing it exists to avoid.
 */

import type { AcceptMethod } from "../lib/disclaimer";
import { currentUserId, requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const BASE = httpBase(import.meta.env.VITE_QV_SET_URL as string | undefined);

const url = (path: string) => `${BASE}${path}?userId=${encodeURIComponent(currentUserId())}`;

/** When this person last accepted this version, or `null` — never, or no answer. */
export async function lastAccepted(version: string): Promise<string | null> {
  if (!BASE) return null;
  try {
    const response = await fetch(url(`/disclaimer/${encodeURIComponent(version)}`), {
      headers: await requestHeaders(),
    });
    if (!response.ok) return null;
    const at = ((await response.json()) as { acceptedAt?: string | null }).acceptedAt;
    return typeof at === "string" ? at : null;
  } catch {
    return null;
  }
}

/** Record the acceptance. Resolves to its time once the server holds it, else `null`. */
export async function recordAccept(version: string, brand: string, method: AcceptMethod): Promise<string | null> {
  if (!BASE) return null;
  try {
    const headers = { ...(await requestHeaders()), "Content-Type": "application/json" };
    const response = await fetch(url(`/disclaimer/${encodeURIComponent(version)}/accept`), {
      method: "POST",
      headers,
      body: JSON.stringify({ brand, method }),
    });
    if (!response.ok) return null;
    const at = ((await response.json()) as { acceptedAt?: string | null }).acceptedAt;
    return typeof at === "string" ? at : null;
  } catch {
    return null;
  }
}
